import React, { useState, useMemo, useEffect } from 'react'
import { Tabs, TabsList, TabsTrigger } from '@/race-tracker/components/ui/tabs'
import { Radio, Users, ClipboardList, Upload, BarChart2 } from 'lucide-react'
import { useAuth } from '@/lib/AuthContext'
import { canAccessTab } from '@/lib/permissions'
import BigPoliticalList from './BigPoliticalList'
import RepFirmManagers from './RepFirmManagers'
import ChangeLog from './ChangeLog'
import ImportTab from './ImportTab'
import StationShare from './StationShare'
import { useStations } from '@/big-political-list/hooks/useBplData'
import '@/big-political-list/bpl-theme.css'

const TABS = [
  { id: 'stations', label: 'Big Political List', icon: Radio },
  { id: 'managers', label: 'Rep Firm Managers', icon: Users },
  { id: 'station-share', label: 'Station Share', icon: BarChart2 },
  { id: 'changelog', label: 'Change Log', icon: ClipboardList },
  { id: 'import', label: 'Import', icon: Upload },
]

export default function BigPoliticalListPage() {
  const [activeTab, setActiveTab] = useState('stations')
  const { user, profile, canWritePage, rolePagePermissions, isSuperAdmin } = useAuth()
  const canWrite = canWritePage('big-political-list')

  const visibleTabs = useMemo(
    () => TABS.filter((t) => canAccessTab('big-political-list', t.id, rolePagePermissions, isSuperAdmin)),
    [rolePagePermissions, isSuperAdmin],
  )

  // Reset to first accessible tab if the current one is revoked
  useEffect(() => {
    if (visibleTabs.length === 0) return
    if (!visibleTabs.find((t) => t.id === activeTab)) {
      setActiveTab(visibleTabs[0].id)
    }
  }, [visibleTabs, activeTab])

  // Loaded at the page level so StationShare can use the BPL ownership data
  // for the Excel export without a separate fetch. TanStack Query deduplicates
  // this with the identical query inside BigPoliticalList when that tab is active.
  const { data: stations = [] } = useStations()

  return (
    <div className="bpl-app min-h-full bg-background text-foreground">
      <header className="bg-navy text-white px-6 py-4">
        <div className="max-w-[1600px] mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
              <Radio className="w-4 h-4 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-lg font-bold tracking-tight">Big Political List</h1>
            </div>
          </div>
        </div>
      </header>

      <div className="bg-card border-b sticky top-0 z-20">
        <div className="max-w-[1600px] mx-auto">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="h-11 bg-transparent border-0 gap-0 p-0 rounded-none">
              {visibleTabs.map((tab) => {
                const Icon = tab.icon
                return (
                  <TabsTrigger
                    key={tab.id}
                    value={tab.id}
                    className="h-11 rounded-none border-b-2 border-transparent px-4 text-xs font-medium text-muted-foreground data-[state=active]:text-foreground data-[state=active]:border-primary data-[state=active]:shadow-none data-[state=active]:bg-transparent"
                  >
                    <Icon className="w-3.5 h-3.5 mr-1.5" />
                    {tab.label}
                  </TabsTrigger>
                )
              })}
            </TabsList>
          </Tabs>
        </div>
      </div>

      <main className="max-w-[1600px] mx-auto p-5">
        {activeTab === 'stations' && (
          <BigPoliticalList canWrite={canWrite} user={user} profile={profile} />
        )}
        {activeTab === 'managers' && (
          <RepFirmManagers canWrite={canWrite} user={user} profile={profile} />
        )}
        {activeTab === 'station-share' && (
          <StationShare canWrite={canWrite} stations={stations} user={user} profile={profile} />
        )}
        {activeTab === 'changelog' && <ChangeLog />}
        {activeTab === 'import' && (
          <ImportTab canWrite={canWrite} isSuperAdmin={isSuperAdmin} user={user} profile={profile} />
        )}
      </main>
    </div>
  )
}
